import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';

interface QuestionTypeDropdownProps {
  onSelectQuestionType: (questionType: string) => void;
  disabled?: boolean;
}

const questionTypes = [
  {
    value: 'multiple_choice',
    label: 'Trắc nghiệm',
    description: 'Câu hỏi có nhiều lựa chọn'
  },
  {
    value: 'true_false',
    label: 'Đúng/Sai',
    description: 'Câu hỏi đúng hoặc sai'
  },
  {
    value: 'fill_blank',
    label: 'Điền vào chỗ trống',
    description: 'Điền từ vào chỗ trống'
  },
  {
    value: 'short_answer',
    label: 'Trả lời ngắn',
    description: 'Câu trả lời ngắn gọn'
  },
  {
    value: 'essay',
    label: 'Tự luận',
    description: 'Câu hỏi tự luận dài'
  }
];

export default function QuestionTypeDropdown({ 
  onSelectQuestionType, 
  disabled = false 
}: QuestionTypeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (questionType: string) => {
    onSelectQuestionType(questionType);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200 min-w-[140px] justify-between"
      >
        <div className="flex items-center">
          <PlusIcon className="w-4 h-4 mr-2" />
          Thêm câu hỏi
        </div>
        <ChevronDownIcon 
          className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
          <div className="py-1">
            {questionTypes.map((type) => (
              <button
                key={type.value}
                onClick={() => handleSelect(type.value)}
                className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="font-medium text-gray-900 dark:text-gray-100">
                  {type.label}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {type.description}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
